/**
 * SIWB适配器服务
 * 支持直连模式和代理模式的统一接口
 */

import { HttpAgent } from "@icp-sdk/core/agent";
import { OdinSiwbClient } from "@/lib/clients/OdinSiwbClient";
import type * as OdinSiwbTypes from "@/lib/idl/odin_siwb_idl";
import { createLogAdapter } from "@/lib/logger/console-adapter";

const uiLog = createLogAdapter("lib/services/siwb-adapter.ts");

export type SiwbMode = "direct" | "proxy";

export class SiwbAdapter {
	private mode: SiwbMode;
	private directClient?: OdinSiwbClient;

	constructor() {
		// 从环境变量读取模式，默认为 direct
		this.mode = (process.env.NEXT_PUBLIC_SIWB_MODE as SiwbMode) || "direct";
		uiLog.info(`🔧 SIWB Adapter initialized in ${this.mode} mode`);
	}

	/**
	 * 获取当前模式
	 */
	getMode(): SiwbMode {
		return this.mode;
	}

	/**
	 * 初始化直连客户端（仅在直连模式使用）
	 */
	private async initDirectClient(): Promise<OdinSiwbClient> {
		if (!this.directClient) {
			const agent = new HttpAgent({
				host: process.env.NEXT_PUBLIC_IC_HOST || "https://ic0.app",
			});

			// 在开发环境中获取根密钥
			if (process.env.NODE_ENV === "development") {
				await agent.fetchRootKey().catch((error) => {
					uiLog.error("fetchRootKey failed:", error);
				});
			}

			this.directClient = new OdinSiwbClient(agent);
		}

		return this.directClient;
	}

	/**
	 * 准备登录
	 */
	async prepareLogin(address: string): Promise<any> {
		uiLog.debug(`🔐 [${this.mode}] Preparing login for address: ${address}`);

		if (this.mode === "direct") {
			const client = await this.initDirectClient();
			return await client.siwb_prepare_login(address);
		} else {
			// 代理模式：调用后端API
			const response = await fetch("/api/siwb/prepare-login", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ address }),
			});

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Failed to prepare login via proxy");
			}

			return await response.json();
		}
	}

	/**
	 * 执行登录
	 */
	async login(
		signature: string,
		address: string,
		publicKeyHex: string | undefined,
		sessionKey: Uint8Array,
		signMessageType: OdinSiwbTypes.SignMessageType,
	): Promise<any> {
		uiLog.debug(`🔐 [${this.mode}] Executing login for address: ${address}`);

		if (this.mode === "direct") {
			const client = await this.initDirectClient();
			return await client.siwb_login(
				signature,
				address,
				publicKeyHex ?? "",
				sessionKey,
				signMessageType,
			);
		} else {
			// 代理模式：调用后端API
			const response = await fetch("/api/siwb/login", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					signature,
					address,
					publicKeyHex: publicKeyHex ?? "",
					sessionKey: Array.from(sessionKey), // 转换为数组以便序列化
					signMessageType,
				}),
			});

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Failed to login via proxy");
			}

			const data = await response.json();

			// 恢复数据类型
			if (data.Ok) {
				// 恢复BigInt类型（如果响应包含expiration字段）
				if (data.Ok.expiration) {
					data.Ok.expiration = BigInt(data.Ok.expiration);
				}
				// 恢复Uint8Array类型（如果响应包含user_canister_pubkey字段）
				if (data.Ok.user_canister_pubkey) {
					data.Ok.user_canister_pubkey = new Uint8Array(
						data.Ok.user_canister_pubkey,
					);
				}
			}

			return data;
		}
	}

	/**
	 * 获取委托
	 */
	async getDelegation(
		address: string,
		sessionKey: Uint8Array,
		expiration: bigint,
	): Promise<any> {
		uiLog.debug(`🔐 [${this.mode}] Getting delegation for address: ${address}`);

		if (this.mode === "direct") {
			const client = await this.initDirectClient();
			return await client.siwb_get_delegation(address, sessionKey, expiration);
		} else {
			// 代理模式：调用后端API
			const response = await fetch("/api/siwb/get-delegation", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					address,
					sessionKey: Array.from(sessionKey),
					expiration: expiration.toString(), // BigInt转字符串
				}),
			});

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Failed to get delegation via proxy");
			}

			const data = await response.json();

			// 恢复数据类型
			if (data.Ok) {
				// 恢复 BigInt 类型
				if (data.Ok.delegation && data.Ok.delegation.expiration) {
					data.Ok.delegation.expiration = BigInt(data.Ok.delegation.expiration);
				}

				// 恢复 Uint8Array 类型
				if (data.Ok.delegation && data.Ok.delegation.pubkey) {
					data.Ok.delegation.pubkey = new Uint8Array(data.Ok.delegation.pubkey);
				}
				if (data.Ok.signature) {
					data.Ok.signature = new Uint8Array(data.Ok.signature);
				}
			}

			return data;
		}
	}

	/**
	 * 获取 Principal
	 */
	async getPrincipal(address: string): Promise<any> {
		uiLog.debug(`🔐 [${this.mode}] Getting principal for address: ${address}`);

		if (this.mode === "direct") {
			const client = await this.initDirectClient();
			return await client.get_principal(address);
		} else {
			// 代理模式：调用后端API
			const response = await fetch("/api/siwb/get-principal", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ address }),
			});

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Failed to get principal via proxy");
			}

			return await response.json();
		}
	}

	/**
	 * 获取解码后的 Principal
	 */
	async getPrincipalDecoded(address: string): Promise<string> {
		uiLog.debug(
			`🔐 [${this.mode}] Getting decoded principal for address: ${address}`,
		);

		if (this.mode === "direct") {
			const client = await this.initDirectClient();
			return await client.get_principal_decoded(address);
		} else {
			// 代理模式下，需要在前端解码
			const response = await this.getPrincipal(address);

			if ("Ok" in response) {
				const { Principal } = await import("@icp-sdk/core/principal");
				const principalBytes = response.Ok;

				// 处理不同的响应格式
				let bytesArray: number[];
				if (Array.isArray(principalBytes)) {
					bytesArray = principalBytes;
				} else if (principalBytes instanceof Uint8Array) {
					bytesArray = Array.from(principalBytes);
				} else if (
					typeof principalBytes === "object" &&
					principalBytes !== null
				) {
					if ("0" in principalBytes) {
						const keys = Object.keys(principalBytes)
							.map(Number)
							.sort((a, b) => a - b);
						bytesArray = keys.map((key) => principalBytes[key]);
					} else {
						throw new Error(`Unexpected principal bytes format`);
					}
				} else {
					throw new Error(`Unexpected principal bytes type`);
				}

				const principal = Principal.fromUint8Array(new Uint8Array(bytesArray));
				return principal.toString();
			} else {
				throw new Error(response.Err || "Failed to get principal");
			}
		}
	}
}

// 导出单例
export const siwbAdapter = new SiwbAdapter();
